'use client';

import React, { useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { Link } from "@/i18n/routing";
interface FormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
}

interface CaptchaChallenge {
  question: string;
  answer: number;
}

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });

  const [captcha, setCaptcha] = useState<CaptchaChallenge>({ question: "", answer: 0 });
  const [captchaInput, setCaptchaInput] = useState<string>("");
  const [captchaValid, setCaptchaValid] = useState<boolean>(false);
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<{ type: "success" | "error" | ""; message: string }>({
    type: "",
    message: "",
  });

  // Generate CAPTCHA
  const generateCaptcha = () => {
    const operations = [
      { op: "+", fn: (a: number, b: number) => a + b },
      { op: "-", fn: (a: number, b: number) => a - b },
      { op: "×", fn: (a: number, b: number) => a * b },
    ];

    const num1 = Math.floor(Math.random() * 20) + 1;
    const num2 = Math.floor(Math.random() * 20) + 1;
    const selectedOp = operations[Math.floor(Math.random() * operations.length)];

    const answer = selectedOp.fn(num1, num2);
    const question = `${num1} ${selectedOp.op} ${num2} = ?`;

    setCaptcha({ question, answer });
    setCaptchaInput("");
    setCaptchaValid(false);
  };

  const validateCaptcha = (value: string) => {
    const isValid = parseInt(value) === captcha.answer;
    setCaptchaValid(isValid);
    return isValid;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCaptchaInput(value);
    validateCaptcha(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    // Required fields: name, email, phone, message (company is optional)
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setStatus({ type: "error", message: "Please fill in all required fields." });
      return;
    }

    // Show CAPTCHA if not shown yet
    if (!showCaptcha) {
      setShowCaptcha(true);
      generateCaptcha();
      setStatus({ type: "", message: "Please solve the CAPTCHA to continue." });
      return;
    }

    if (!captchaValid) {
      setStatus({ type: "error", message: "Incorrect CAPTCHA answer. Please try again." });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: "success", message: "Thanks! Your message has been sent." });
        setFormData({ name: "", company: "", email: "", phone: "", message: "" });
        setShowCaptcha(false);
        setCaptchaInput("");
        setCaptchaValid(false);
      } else {
        setStatus({ type: "error", message: data.error || "Something went wrong. Please try again." });
      }
    } catch (error) {
      setStatus({ type: "error", message: "Network error. Please try again later." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-8 lg:gap-16 m-2 lg:m-8 p-4 md:p-10 rounded-2xl bg-primary">
      {/* Top row */}
      <div className="w-full flex gap-4 max-[900px]:flex-col max-[900px]:gap-1">
        <div className="flex-1 text-center max-[900px]:text-center">
          <p className="primary text-4xl lg:text-6xl font-bold text-white">
            Let’s create together
          </p>
        </div>
        <div className="flex-1 text-center">
          <p className="primary text-sm text-white">Free Consultation</p>
        </div>
        <div className="flex-1 text-right max-[900px]:text-center">
          <p className="primary text-sm text-white">&copy; 2026</p>
        </div>
      </div>

      {/* Bottom row */}
      <div className="flex gap-4 max-[900px]:flex-col max-[900px]:gap-16">
        {/* Left column */}
        <div className="flex-1 flex flex-col justify-between gap-16 max-[900px]:gap-8">
          <div className="flex flex-col gap-6">
            <h3 className="primary text-xl lg:text-3xl font-bold text-white max-[900px]:text-center">
              Start a Conversation
            </h3>
            <p className="text-base lg:text-xl font-medium text-white mb-2 w-3/4  max-[900px]:w-full max-[900px]:text-center">
              Have a story in mind? Let’s bring it to life. I’d love to hear
              what you’re working on and explore how we can collaborate.
            </p>
          </div>

          <div className="w-full lg:w-3/4 flex justify-between gap-4 border-t border-dashed border-white py-2 max-[900px]:flex-col max-[900px]:text-center max-[900px]:gap-1">
            <p className="primary text-xs lg:text-sm text-white">Open for Partnerships</p>
            <p className="primary text-xs lg:text-sm text-white">Clients worldwide</p>
          </div>
        </div>

        {/* Right column (form) */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-3 -mt-10">
          <div className="flex gap-2 w-full flex-wrap md:flex-nowrap">
            <div className="w-full rounded-3xl bg-secondary">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Full name *"
                className="w-full bg-transparent text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none"
              />
            </div>
            <div className="w-full rounded-3xl bg-secondary">
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Company"
                className="w-full bg-transparent text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none"
              />
            </div>
          </div>

          <div className="flex gap-2 w-full flex-wrap md:flex-nowrap">
            <div className="w-full rounded-3xl bg-secondary">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email *"
                className="w-full bg-transparent text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none"
              />
            </div>
            <div className="w-full rounded-3xl bg-secondary">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Phone *"
                className="w-full bg-transparent text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none"
              />
            </div>
          </div>

          <div className="rounded-3xl bg-secondary">
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={6}
              placeholder="Project Details *"
              className="w-full bg-transparent text-white placeholder:text-white px-4 py-3 rounded-3xl outline-none resize-none"
            />
          </div>

          {/* CAPTCHA - only shows after first submit attempt */}
          {showCaptcha && (
            <div className="items-center flex flex-col gap-3 py-2">
              <div className="bg-white/90 px-4 py-2 w-60 rounded-3xl shadow-sm flex justify-around items-center gap-2">
                <p className="text-xl font-bold text-center text-secondary font-mono tracking-wider">
                  {captcha.question}
                </p>
                <FiRefreshCw
                  size={20}
                  className="text-secondary cursor-pointer"
                  onClick={() => {
                    generateCaptcha();
                    setCaptchaInput("");
                  }}
                />
              </div>

              <input
                type="number"
                value={captchaInput}
                onChange={handleCaptchaChange}
                placeholder="Answer"
                className={`px-4 w-60 py-2 rounded-3xl outline-none transition-all text-white bg-secondary border ${
                  captchaInput === ""
                    ? "border-white/30"
                    : captchaValid
                    ? "border-green-400"
                    : "border-red-400"
                }`}
              />
            </div>
          )}

          {status.message && (
            <p
              className={`text-xs lg:text-sm text-center font-medium ${
                status.type === "success"
                  ? "text-green-400"
                  : status.type === "error"
                  ? "text-red-400"
                  : "text-white"
              }`}
            >
              {status.message}
            </p>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || (showCaptcha && !captchaValid)}
              className="w-full items-center py-3 rounded-3xl bg-white font-semibold text-secondary hover:bg-accent hover:text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : showCaptcha && !captchaValid ? (
                "Solve CAPTCHA"
              ) : (
                "Submit"
              )}
            </button>
            <p className="text-xs lg:text-sm text-white pt-4">
              By submitting this form, you agree to receive communications from Ignite Visibility,
              including marketing emails and SMS messages if applicable. You also agree to our{" "}
            <Link href={"/privacy-policy"}><span className="underline">Privacy Policy</span></Link> and{" "}
              <Link href={"/terms-and-conditions"}><span className="underline">Terms of Service</span></Link> .
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;